import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

// Cada diploma usa una plantilla ilustrada de fondo (/diplomas/*.png) y superpone
// el nombre del paciente, la firma del médico y la fecha en las cajas del diseño.
export const DIPLOMAS = [
  {
    key: "valiente", scope: "both", title: "¡Eres muy valiente!", team: "ClinPed", accent: "#1746a2",
    img: "/diplomas/valiente.png", ar: 1264 / 848, qrSide: "right",
    name: { left: 33.6, top: 61.9, width: 45.5, height: 8.9, color: "#153a86" },
    firma: { cx: 16.6, bottom: 10.6, width: 14 },
    fecha: { top: 87.8, color: "#153a86", cols: [44.3, 51.6, 60.0] },
  },
  {
    key: "vacunas", scope: "clinped", title: "Campeón de las Vacunas", team: "ClinPed", accent: "#1e63c8",
    img: "/diplomas/vacunas.png", ar: 1426 / 1103, qrSide: "right",
    name: { left: 32.3, top: 62.5, width: 46.2, height: 8.2, color: "#153a86" },
    firma: { cx: 18.2, bottom: 11.0, width: 18 },
    fecha: { top: 88.6, color: "#153a86", cols: [47.7, 55.4, 63.1] },
  },
  {
    key: "cirugia", scope: "clinped", title: "¡Todo salió muy bien!", team: "ClinPed", accent: "#1e88a8",
    img: "/diplomas/cirugia.png", ar: 1264 / 848, qrSide: "right",
    name: { left: 35.6, top: 59.6, width: 46.3, height: 10.6, color: "#153a86" },
    firma: { cx: 18.2, bottom: 10.0, width: 16 },
    fecha: { top: 89.4, color: "#153a86", cols: [45.5, 52.6, 61.3] },
  },
  {
    key: "sonrisa", scope: "clindent", title: "Sonrisa Brillante", team: "ClinDent", accent: "#188a9a",
    img: "/diplomas/sonrisa.png", ar: 1426 / 1103, qrSide: "left",
    name: { left: 14.4, top: 58.9, width: 42.4, height: 10.0, color: "#146a78" },
    firma: { cx: 17.0, bottom: 9.5, width: 24 },
    fecha: { top: 89.6, color: "#146a78", cols: [48.0, 56.1, 64.2] },
  },
  {
    key: "diente", scope: "clindent", title: "Mi Primer Diente Arreglado", team: "ClinDent", accent: "#188a9a",
    img: "/diplomas/diente.png", ar: 1426 / 1103, qrSide: "right",
    name: { left: 31.4, top: 65.7, width: 42.9, height: 6.8, color: "#146a78" },
    firma: { cx: 18.2, bottom: 11.5, width: 18 },
    fecha: { top: 91.0, color: "#146a78", cols: [45.9, 53.6, 61.4] },
  },
];

export const diplomaByKey = (k) => DIPLOMAS.find((d) => d.key === k) || DIPLOMAS[0];

const nameFontPx = (len) => (len <= 11 ? 40 : len <= 16 ? 34 : len <= 22 ? 28 : len <= 30 ? 23 : 19);

// Ancho fijo para exportar en buena calidad.
const W = 780;

export const DiplomaCard = forwardRef(function DiplomaCard(
  { type, patientName, date, qrUrl, signatureUrl }, ref
) {
  const d = diplomaByKey(type);
  const H = Math.round(W / d.ar);
  const nombre = (patientName || "").trim();
  const now = date ? new Date(date) : new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yy = String(now.getFullYear());
  const dateParts = [dd, mm, yy];

  return (
    <div
      ref={ref}
      style={{ width: W, height: H, position: "relative", background: "#fff", fontFamily: "'Nunito', sans-serif", overflow: "hidden" }}
    >
      <img
        src={d.img}
        alt={d.title}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
      />

      {/* Nombre del paciente */}
      <span
        style={{
          position: "absolute",
          left: `${d.name.left + d.name.width / 2}%`,
          top: `${d.name.top + d.name.height / 2}%`,
          transform: "translate(-50%, -50%)",
          fontFamily: "'Baloo 2', 'Nunito', sans-serif",
          fontWeight: 800, color: d.name.color,
          fontSize: nameFontPx(nombre.length), lineHeight: 1,
          whiteSpace: "nowrap", textAlign: "center",
        }}
      >
        {nombre}
      </span>

      {/* Firma del médico */}
      {signatureUrl && (
        <img
          src={signatureUrl}
          alt="Firma"
          crossOrigin="anonymous"
          style={{
            position: "absolute",
            left: `${d.firma.cx}%`, bottom: `${d.firma.bottom}%`,
            transform: "translateX(-50%)",
            width: `${d.firma.width}%`, maxHeight: 52, objectFit: "contain",
          }}
        />
      )}

      {/* Fecha (día / mes / año sobre los espacios) */}
      {d.fecha.cols.map((cx, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${cx}%`, top: `${d.fecha.top}%`,
            transform: "translate(-50%, -50%)",
            fontFamily: "'Baloo 2', 'Nunito', sans-serif",
            fontWeight: 800, color: d.fecha.color, fontSize: 20,
          }}
        >
          {dateParts[i]}
        </span>
      ))}

      {/* QR opcional (sólo en la descarga) */}
      {qrUrl && (
        <div
          style={{
            position: "absolute", top: "2.6%",
            [d.qrSide === "left" ? "left" : "right"]: "2.6%",
            background: "#fff", borderRadius: 10, padding: 6,
            border: `2px solid ${d.accent}55`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)", textAlign: "center",
          }}
        >
          <QRCodeCanvas value={qrUrl} size={68} fgColor="#1E293B" level="M" />
          <p style={{ fontSize: 9, color: "#64748b", margin: "2px 0 0", fontWeight: 700 }}>Escanéame</p>
        </div>
      )}
    </div>
  );
});
